import { Link } from "@tanstack/react-router";
import { BUSINESS } from "@/lib/business";
import { SERVICES } from "@/lib/services";
import { CITIES } from "@/lib/cities";
import logo from "@/assets/logo.webp";
import { Facebook, Instagram, Youtube, Twitter } from "lucide-react";
import { PreferredSourceButtonCompact } from "./PreferredSourceButton";

export function Footer() {
  return (
    <footer className="mt-20 bg-secondary text-secondary-foreground">
      <div className="mx-auto max-w-7xl px-4 py-12 grid gap-10 md:grid-cols-4">
        <div>
          <div className="flex items-center gap-3">
            <img decoding="async" src={logo} alt="Phoenix Plumbing Co. logo" width={48} height={48} className="h-12 w-12 rounded bg-white" />
            <div className="font-extrabold">Phoenix Plumbing Co.</div>
          </div>
          <p className="mt-3 text-sm opacity-80">
            {BUSINESS.credentialsLine}.
          </p>
          <p className="mt-3 text-sm">
            <a href={BUSINESS.phoneHref} className="block font-extrabold text-accent text-lg">
              {BUSINESS.phone}
            </a>
            <a href={`mailto:${BUSINESS.email}`} className="block opacity-90 mt-1">{BUSINESS.email}</a>
            <span className="block opacity-80 mt-2">{BUSINESS.address}<br/>{BUSINESS.city}, {BUSINESS.state} {BUSINESS.zip}</span>
            <span className="block opacity-70 mt-2 text-xs">License: {BUSINESS.license}</span>
          </p>
          <div className="mt-4 flex gap-3 text-secondary-foreground/80">
            <a href={BUSINESS.socials.facebook} aria-label="Facebook"><Facebook className="h-5 w-5" /></a>
            <a href={BUSINESS.socials.instagram} aria-label="Instagram"><Instagram className="h-5 w-5" /></a>
            <a href={BUSINESS.socials.youtube} aria-label="YouTube"><Youtube className="h-5 w-5" /></a>
            <a href={BUSINESS.socials.twitter} aria-label="Twitter"><Twitter className="h-5 w-5" /></a>
          </div>
          <div className="mt-5">
            <a
              id="COCBadgeURL"
              href="https://www.chamberofcommerce.com/business-directory/arizona/phoenix/plumber/2034523343-phoenix-plumbing-co?source=memberwebsite"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="View our Chamber of Commerce business profile"
            >
              <img
                id="COCBadgeIMG"
                src="https://coc.codes/images/badge/2034523343"
                alt="Chamber of Commerce member badge — Phoenix Plumbing Co."
                width={110}
                height={150}
                loading="lazy"
                decoding="async"
                className="h-[150px] w-auto rounded bg-white p-1"
              />
            </a>
          </div>
        </div>

        <div>
          <h3 className="font-bold mb-3 text-accent">Services</h3>
          <ul className="space-y-1.5 text-sm columns-1">
            {SERVICES.map((s) => (
              <li key={s.slug}>
                <Link to={`/${s.slug}` as any} className="opacity-90 hover:opacity-100 hover:underline">{s.name}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="font-bold mb-3 text-accent">Areas Served</h3>
          <ul className="space-y-1.5 text-sm columns-2">
            {CITIES.map((c) => (
              <li key={c.slug}>
                <Link
                  to="/areas-served/$city"
                  params={{ city: c.slug }}
                  className="opacity-90 hover:opacity-100 hover:underline"
                >
                  {c.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="font-bold mb-3 text-accent">Hours</h3>
          <ul className="space-y-1 text-sm">
            {BUSINESS.hours.map((h) => (
              <li key={h.days}><strong>{h.days}:</strong> {h.time}</li>
            ))}
          </ul>
          <p className="mt-2 text-xs opacity-80">{BUSINESS.afterHoursNote}</p>
          <h3 className="font-bold mt-5 mb-2 text-accent">Follow in Google</h3>
          <PreferredSourceButtonCompact />
          <h3 className="font-bold mt-5 mb-2 text-accent">Quick links</h3>
          <ul className="space-y-1 text-sm">
            <li><Link to="/financing" className="opacity-90 hover:underline">Financing</Link></li>
            <li><Link to="/coupons" className="opacity-90 hover:underline">Coupons</Link></li>
            <li><Link to="/sitemap" className="opacity-90 hover:underline">Sitemap</Link></li>
            <li><Link to="/privacy-policy" className="opacity-90 hover:underline">Privacy</Link></li>
            <li><Link to="/terms-of-service" className="opacity-90 hover:underline">Terms</Link></li>
            <li>
              <a
                href={`https://www.google.com/maps?cid=${BUSINESS.cid}`}
                target="_blank"
                rel="noopener noreferrer"
                className="opacity-90 hover:underline"
              >
                Find us on Google
              </a>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 bg-secondary/80">
        <div className="mx-auto max-w-7xl px-4 py-6">
          <h3 className="font-bold text-accent mb-2 text-sm">Find Us</h3>
          <div className="aspect-[16/6] w-full overflow-hidden rounded-lg border border-white/10">
            <iframe
              title="Phoenix Plumbing Co. on Google Maps"
              src={BUSINESS.mapEmbed}
              loading="lazy"
              className="h-full w-full"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="mx-auto max-w-7xl px-4 py-4 text-xs opacity-70 flex flex-wrap justify-between gap-2">
          <span>© {new Date().getFullYear()} {BUSINESS.name}. All rights reserved.</span>
          <a href={`https://www.google.com/maps?cid=${BUSINESS.cid}`} className="hover:underline">Get Directions</a>
        </div>
      </div>
    </footer>
  );
}